import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtINR, fmtDateTime, fmtDate } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { SectionTitle, PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import {
  Users, Briefcase, Target, Package, ListChecks, ClipboardText,
  Warning, Buildings, CheckCircle, CurrencyInr, ChartLineUp, Cube,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const STAGE_COLORS = ["#fd7e14", "#e96d05", "#c25600", "#9c4400", "#763200", "#fd9d4a", "#fdb87a", "#FFCB99", "#16A34A", "#9C2B2B", "#DC2626"];

const ClickableKpi = ({ label, value, icon: Icon, accent = "", to, testid }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={to ? () => navigate(to) : undefined}
      data-testid={testid}
      className={`group text-left rounded-lg border bg-[hsl(var(--card))] p-5 transition-all hover:shadow-md hover:-translate-y-0.5 ${to ? "cursor-pointer hover:border-[#fd7e14]" : "cursor-default"}`}
      style={{ borderColor: "hsl(var(--border))" }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-[0.15em] font-semibold opacity-60">{label}</span>
        {Icon && <Icon size={20} className="opacity-50 group-hover:opacity-90 group-hover:text-[#fd7e14] transition-colors" weight="duotone" />}
      </div>
      <div className={`text-2xl sm:text-3xl font-semibold ${accent}`} style={{ fontFamily: "Outfit, sans-serif" }}>
        {value}
      </div>
    </button>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [myItems, setMyItems] = useState([]);
  const [overdueItems, setOverdueItems] = useState([]);

  const isAdmin = hasRole(user, "super_admin", "hr");
  const isSalesView = hasRole(user, "super_admin", "sales_manager", "department_manager");
  const isApprover = hasRole(user, "super_admin", "hr", "department_manager", "sales_manager");

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/inventory/my-items").then((r) => setMyItems(r.data)).catch(() => {});
    if (isAdmin) {
      api.get("/inventory/overdue").then((r) => setOverdueItems(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  if (!stats) {
    return (
      <div>
        <PageHeader title="Loading…" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array(8).fill(0).map((_, i) => <Card key={i} className="h-28 animate-pulse" />)}
        </div>
      </div>
    );
  }

  const stageData = Object.entries(stats.by_stage || {}).map(([stage, v]) => ({ stage, value: v.value, count: v.count }));

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={`Good ${getDayPart()}, ${user.full_name?.split(" ")[0] || "there"}`}
        description={`Here's what's happening — ${new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}.`}
      />

      {/* Inventory alerts strip — for everyone with assigned items */}
      {myItems.length > 0 && (
        <div className="mb-6">
          <SectionTitle>My Assigned Items</SectionTitle>
          <Card className="p-0 overflow-hidden">
            <ul className="divide-y" style={{ borderColor: "hsl(var(--border))" }}>
              {myItems.map((it) => (
                <li key={it.id} className={`px-5 py-3 flex items-center justify-between text-sm ${it.is_overdue ? "bg-red-50/60 dark:bg-red-950/20" : ""}`} data-testid={`my-item-${it.id}`}>
                  <div className="min-w-0 flex items-center gap-3">
                    <Cube size={20} weight="duotone" className={it.is_overdue ? "text-red-600" : "text-[#fd7e14]"} />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{it.product_name}</div>
                      <div className="text-xs opacity-70 truncate">SN: {it.serial_number}{it.purpose ? ` • ${it.purpose}` : ""}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {it.expected_return_date && (
                      <div className={`text-xs ${it.is_overdue ? "text-red-700 font-semibold" : "opacity-70"}`}>
                        {it.is_overdue && <Warning size={12} weight="fill" className="inline mr-1" />}
                        Return by {fmtDate(it.expected_return_date)}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      {isAdmin && overdueItems.length > 0 && (
        <div className="mb-6">
          <Card className="p-4 border-red-200 bg-red-50/60 dark:bg-red-950/20">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-medium mb-2">
              <Warning size={18} weight="fill" /> Overdue Items ({overdueItems.length})
            </div>
            <ul className="space-y-1 text-sm">
              {overdueItems.slice(0, 5).map((it) => (
                <li key={it.id} className="flex items-center justify-between" data-testid={`overdue-${it.id}`}>
                  <span><b>{it.product_name}</b> with {it.assigned_to ? "an employee" : "—"} • SN {it.serial_number}</span>
                  <span className="text-xs opacity-70">Due {fmtDate(it.expected_return_date)}</span>
                </li>
              ))}
              {overdueItems.length > 5 && <li className="text-xs opacity-70">…and {overdueItems.length - 5} more</li>}
            </ul>
          </Card>
        </div>
      )}

      {isSalesView && (
        <>
          <SectionTitle>Revenue Pipeline</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            <ClickableKpi testid="kpi-pipeline" label="Pipeline" value={fmtINR(stats.pipeline_value)} icon={ChartLineUp} accent="text-[#fd7e14]" to="/opportunities?stage=Pipeline" />
            <ClickableKpi testid="kpi-upside" label="Upside" value={fmtINR(stats.upside_value)} icon={ChartLineUp} accent="text-amber-600 dark:text-amber-400" to="/opportunities?stage=Upside" />
            <ClickableKpi testid="kpi-commit" label="Commit" value={fmtINR(stats.commit_value)} icon={CheckCircle} accent="text-green-700 dark:text-green-400" to="/opportunities?stage=Commit" />
            <ClickableKpi testid="kpi-invoiced" label="Invoiced" value={fmtINR(stats.invoiced_value)} icon={CurrencyInr} accent="text-[#fd7e14]" to="/opportunities?stage=Invoiced" />
            <ClickableKpi testid="kpi-rr" label="RR" value={fmtINR(stats.rr_value)} icon={CurrencyInr} to="/opportunities?stage=RR" />
          </div>
        </>
      )}

      <SectionTitle>Operations</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {isAdmin && <ClickableKpi testid="kpi-employees" label="Employees" value={stats.total_employees} icon={Users} to="/employees" />}
        <ClickableKpi testid="kpi-accounts" label="Accounts" value={stats.total_accounts} icon={Briefcase} to="/accounts" />
        <ClickableKpi testid="kpi-opps" label="Opportunities" value={stats.total_opportunities} icon={Target} to="/opportunities" />
        <ClickableKpi testid="kpi-products" label="Products" value={stats.total_products} icon={Package} to="/products" />
        <ClickableKpi testid="kpi-tasks" label="Tasks" value={stats.total_tasks} icon={ListChecks} to="/tasks" />
        <ClickableKpi testid="kpi-overdue" label="Overdue Tasks" value={stats.overdue_tasks} icon={Warning} accent={stats.overdue_tasks > 0 ? "text-red-600 dark:text-red-400" : ""} to="/tasks?status=overdue" />
        {isApprover && <ClickableKpi testid="kpi-approvals" label="Pending Approvals" value={stats.pending_approvals} icon={ClipboardText} to="/approvals" />}
        {isSalesView && <ClickableKpi testid="kpi-ongoing" label="Ongoing Projects" value={stats.ongoing_projects} icon={Buildings} to={`/opportunities?stage=${encodeURIComponent("Ongoing Project")}`} />}
      </div>

      {isSalesView && (
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Opportunities by Stage</h3>
              <span className="text-xs opacity-60">{stageData.reduce((s, d) => s + d.count, 0)} total</span>
            </div>
            {stageData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm opacity-50">No opportunities yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={stageData} margin={{ left: 0, right: 0, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="stage" tick={{ fontSize: 10, fill: "currentColor" }} interval={0} angle={-25} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 11, fill: "currentColor" }} />
                  <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }} />
                  <Bar dataKey="value" fill="#fd7e14" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Pipeline Distribution</h3>
            </div>
            {stageData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm opacity-50">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={stageData} dataKey="value" nameKey="stage" cx="50%" cy="50%" outerRadius={90} label={(d) => d.stage.split(" ")[0]}>
                    {stageData.map((_, i) => <Cell key={i} fill={STAGE_COLORS[i % STAGE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>
      )}

      {isAdmin && (
        <>
          <SectionTitle>Recent Activity</SectionTitle>
          <Card className="p-0 overflow-hidden">
            {(!stats.recent_activity || stats.recent_activity.length === 0) ? (
              <div className="p-10 text-center text-sm opacity-60">No activity yet.</div>
            ) : (
              <ul className="divide-y" style={{ borderColor: "hsl(var(--border))" }}>
                {stats.recent_activity.slice(0, 10).map((r) => (
                  <li key={r.id} className="px-6 py-3 flex items-center justify-between text-sm">
                    <div className="min-w-0">
                      <span className="font-medium capitalize">{r.action.replace(/_/g, " ")}</span>
                      <span className="opacity-60"> • {r.entity_type}</span>
                    </div>
                    <div className="text-xs opacity-50 ml-4 shrink-0">{fmtDateTime(r.created_at)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function getDayPart() {
  const h = new Date().getHours();
  return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
}
